import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { TrackedTextField } from "src/theme/StyledFields";
import { sms, smsVerify } from "../allauth/lib/allauth";

interface ChangeCellPhoneDialogProps {
    open: boolean;
    onClose: () => void;
    /** Current saved number (any format). Presence toggles "Add" vs "Change" copy/consent. */
    currentPhone?: string | null;
    /** Called with the confirmed E.164 number after a successful verification. */
    onSuccess: (phone: string) => void;
}

type Step = "phone" | "code";

function extractErrorMessage(content: unknown, fallback: string): string {
    if (!content || typeof content !== "object") {
        return fallback;
    }
    const data = content as Record<string, unknown>;
    if (typeof data.error === "string" && data.error.trim()) {
        return data.error;
    }
    if (Array.isArray(data.phone) && data.phone[0]) {
        return String(data.phone[0]);
    }
    return fallback;
}

function normalizeTenDigits(raw: string | null | undefined): string {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
        return digits.slice(1);
    }
    return digits.length === 10 ? digits : "";
}

const ChangeCellPhoneDialog: React.FC<ChangeCellPhoneDialogProps> = ({
    open,
    onClose,
    currentPhone,
    onSuccess,
}) => {
    const hasCurrent = !!normalizeTenDigits(currentPhone);

    const [step, setStep] = useState<Step>("phone");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [consentAgreed, setConsentAgreed] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [info, setInfo] = useState<string>("");
    const [busy, setBusy] = useState<boolean>(false);

    // Consent is only collected when adding a number that wasn't previously saved.
    const needsConsent = !hasCurrent;

    // Reset the flow every time the dialog is (re)opened.
    useEffect(() => {
        if (open) {
            setStep("phone");
            setPhoneNumber("");
            setVerificationCode("");
            setConsentAgreed(false);
            setError("");
            setInfo("");
            setBusy(false);
        }
    }, [open]);

    const validatePhoneNumber = (number: string): boolean => /^[0-9]{10}$/.test(number);

    const handleSendCode = async () => {
        if (!validatePhoneNumber(phoneNumber)) {
            setError("Enter a valid US phone number (10 digits, no spaces).");
            return;
        }
        if (needsConsent && !consentAgreed) {
            setError("Please agree to receive SMS to save a phone number.");
            return;
        }
        setError("");
        setInfo("");
        setBusy(true);
        try {
            const content = await sms({ phone: phoneNumber, intent: "change" });
            if (content && typeof content === "object" && ("error" in content || "phone" in content)) {
                setError(extractErrorMessage(content, "Failed to send verification code. Please try again."));
                return;
            }
            setStep("code");
            setInfo(`We sent a verification code to (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}.`);
        } catch (err) {
            console.error(err);
            setError("Failed to send verification code. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleVerifyCode = async () => {
        const code = verificationCode.trim();
        if (!code) {
            setError("Enter the verification code we texted you.");
            return;
        }
        setError("");
        setBusy(true);
        try {
            const content = await smsVerify({ phone: phoneNumber, code, intent: "change" });
            if (content && typeof content === "object" && "id" in content && (content as { id?: number }).id) {
                const confirmed = (content as { cell_phone?: string }).cell_phone || phoneNumber;
                onSuccess(confirmed);
                onClose();
                return;
            }
            setError(extractErrorMessage(content, "Verification failed. Please try again."));
        } catch (err) {
            console.error(err);
            setError("Verification failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth data-attr="change-cell-phone_dialog">
            <DialogTitle>{hasCurrent ? "Change Cell Phone" : "Add Cell Phone"}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {info && step === "code" && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

                {step === "phone" ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <DialogContentText>
                            Enter your cell number. We'll text you a verification code, and your number is
                            only saved once the code is confirmed.
                        </DialogContentText>
                        <TrackedTextField
                            autoFocus
                            fullWidth
                            label="Cell Phone"
                            value={phoneNumber}
                            onChange={(e) => { setPhoneNumber(e.target.value); setError(""); }}
                            placeholder="4155551212"
                            inputProps={{ maxLength: 10, inputMode: "tel" }}
                            data-attr="change-cell-phone_field-phone"
                        />
                        {needsConsent && (
                            <FormControlLabel
                                sx={{ alignItems: "flex-start" }}
                                control={
                                    <Checkbox
                                        checked={consentAgreed}
                                        onChange={(e) => { setConsentAgreed(e.target.checked); setError(""); }}
                                        name="sms-consent"
                                        aria-describedby="change-cell-phone-consent-description"
                                        data-attr="change-cell-phone_field-consent"
                                    />
                                }
                                label={
                                    <Typography variant="body2" id="change-cell-phone-consent-description">
                                        I agree to receive SMS from Strong Mind Strong Performance per the{" "}
                                        <RouterLink to="/terms-of-use" target="_blank" rel="noopener noreferrer">
                                            Terms of Use
                                        </RouterLink>
                                        . Message and data rates may apply.
                                    </Typography>
                                }
                            />
                        )}
                    </Stack>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TrackedTextField
                            autoFocus
                            fullWidth
                            label="Verification Code"
                            placeholder="000000"
                            value={verificationCode}
                            onChange={(e) => { setVerificationCode(e.target.value); setError(""); }}
                            inputProps={{ maxLength: 6, inputMode: "numeric" }}
                            data-attr="change-cell-phone_field-code"
                        />
                        <Button
                            size="small"
                            disabled={busy}
                            onClick={() => { setStep("phone"); setVerificationCode(""); setError(""); setInfo(""); }}
                            data-attr="change-cell-phone_use-different-number"
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Use a different number
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} data-attr="change-cell-phone_cancel">
                    Cancel
                </Button>
                {step === "phone" ? (
                    <Button
                        variant="contained"
                        onClick={handleSendCode}
                        disabled={busy || phoneNumber.replace(/\D/g, "").length < 10 || (needsConsent && !consentAgreed)}
                        data-attr="change-cell-phone_send-code"
                    >
                        {busy ? "Sending..." : "Send Code"}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleVerifyCode}
                        disabled={busy || !verificationCode.trim()}
                        data-attr="change-cell-phone_verify-code"
                    >
                        {busy ? "Verifying..." : "Verify & Save"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ChangeCellPhoneDialog;
